import { useEffect, useMemo, useState } from "react";
import { MapContainer, Marker, TileLayer, CircleMarker, Tooltip as LeafletTooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Box, ToggleButton, ToggleButtonGroup, Typography, alpha, useTheme } from "@mui/material";
import { TouchAppOutlined } from "@mui/icons-material";

import { googleTiles } from "./BaseMapLayer";

// Same Google tiles as the Radar / Review pages (see BaseMapLayer): r road map, y satellite
const DEFAULT_CENTER = [-6.369028, 34.888822];   // Tanzania, same default as the Radar page

const isNum = (v) => v !== "" && v !== null && v !== undefined && Number.isFinite(Number(v));

// Pin drawn in HTML: Leaflet's default marker images don't load in this build
const pinIcon = (color) =>
  L.divIcon({
    className: "position-picker-pin",
    html: `
      <div style="position:relative;width:28px;height:28px;transform:translate(-50%,-100%);">
        <svg viewBox="0 0 24 24" width="28" height="28" style="filter:drop-shadow(0 2px 3px rgba(0,0,0,.6))">
          <path fill="${color}" stroke="#1a1205" stroke-width="1"
            d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7z"/>
          <circle cx="12" cy="9" r="2.6" fill="#1a1205"/>
        </svg>
      </div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });

// Map inside a dialog: it is measured before the dialog has finished opening, so tell
// Leaflet its real size once it is visible
const FixSize = () => {
  const map = useMap();
  useEffect(() => {
    const t1 = setTimeout(() => map.invalidateSize(), 50);
    const t2 = setTimeout(() => map.invalidateSize(), 350);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [map]);
  return null;
};

const ClickToPick = ({ onPick }) => {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  });
  return null;
};

// When the coordinates are typed in, bring the pin into view
const FollowValue = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (!position) return;
    if (!map.getBounds().pad(-0.1).contains(position)) {
      map.setView(position, Math.max(map.getZoom(), 9), { animate: true });
    }
  }, [map, position]);
  return null;
};

/**
 * Pick a position on a Google map: click the map or drag the pin.
 * lat / lng: current values (numbers or strings, '' = not set)
 * onChange(lat, lng): numbers, rounded to 6 decimals (~10 cm)
 * others: [{ id, name, latitude, longitude }] shown as small dots for reference (e.g. other outposts)
 */
const PositionPicker = ({ lat, lng, onChange, others = [], height = 260 }) => {
  const theme = useTheme();
  const accent = theme.palette.secondary.main;
  const [layer, setLayer] = useState("r");

  const position = isNum(lat) && isNum(lng) ? [Number(lat), Number(lng)] : null;
  const icon = useMemo(() => pinIcon(accent), [accent]);

  const pick = (la, ln) => onChange(Number(la.toFixed(6)), Number(ln.toFixed(6)));

  return (
    <Box
      sx={{
        position: "relative",
        height,
        borderRadius: 2,
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        "& .leaflet-container": { height: "100%", width: "100%", cursor: "crosshair", fontFamily: "inherit" },
      }}
    >
      <MapContainer
        center={position || DEFAULT_CENTER}
        zoom={position ? 10 : 6}
        zoomSnap={0.5}
        scrollWheelZoom
        attributionControl={false}
      >
        <TileLayer url={googleTiles(layer)} />
        <FixSize />
        <ClickToPick onPick={pick} />
        <FollowValue position={position} />

        {others
          .filter((o) => isNum(o.latitude) && isNum(o.longitude))
          .map((o) => (
            <CircleMarker
              key={o.id}
              center={[Number(o.latitude), Number(o.longitude)]}
              radius={4}
              pathOptions={{ color: "#ffffff", weight: 1, fillColor: "#ff3b3b", fillOpacity: 0.9 }}
            >
              <LeafletTooltip direction="top" offset={[0, -4]}>{o.name}</LeafletTooltip>
            </CircleMarker>
          ))}

        {position && (
          <Marker
            position={position}
            icon={icon}
            draggable
            eventHandlers={{
              dragend: (e) => {
                const p = e.target.getLatLng();
                pick(p.lat, p.lng);
              },
            }}
          />
        )}
      </MapContainer>

      {/* Map / Satellite */}
      <ToggleButtonGroup
        exclusive
        size="small"
        value={layer}
        onChange={(e, v) => { if (v) setLayer(v); }}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 1000,
          backgroundColor: "rgba(10,14,18,0.75)",
          borderRadius: "8px",
          "& .MuiToggleButton-root": {
            px: 1,
            py: 0.25,
            border: "none",
            borderRadius: "8px !important",
            color: "rgba(255,255,255,0.8)",
            fontSize: "0.7rem",
            fontWeight: 700,
            textTransform: "none",
            "&.Mui-selected, &.Mui-selected:hover": { color: "#1a1205", backgroundColor: accent },
          },
        }}
      >
        <ToggleButton value="r">Map</ToggleButton>
        <ToggleButton value="y">Satellite</ToggleButton>
      </ToggleButtonGroup>

      {/* Hint */}
      <Box
        sx={{
          position: "absolute",
          left: 8,
          bottom: 8,
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: "6px",
          pointerEvents: "none",
          color: "#fff",
          backgroundColor: alpha("#000", 0.6),
        }}
      >
        <TouchAppOutlined sx={{ fontSize: 14 }} />
        <Typography sx={{ fontSize: "0.68rem" }}>
          {position ? "Click the map or drag the pin to move it" : "Click the map to set the position"}
        </Typography>
      </Box>
    </Box>
  );
};

export default PositionPicker;
